// Relay.link API client for cross-chain USDC withdrawals

const RELAY_BASE_URL = 'https://api.relay.link'

// Chain configurations
export const CHAIN_CONFIG = {
	polygon: {
		id: 137,
		name: 'Polygon',
		// pUSD — Polymarket V2 collateral. After cutover Safes hold pUSD, not USDC.e.
		// Relay.link supports pUSD as a token. If quotes start failing on bridge,
		// revert to USDC.e and insert a Collateral Offramp unwrap step before transfer.
		usdc: '0xC011a7E12a19f7B1f670d46F03B03f3342E82DFB',
		explorer: 'https://polygonscan.com',
	},
	solana: {
		id: 792703809,
		name: 'Solana',
		usdc: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
		explorer: 'https://solscan.io',
	},
	bsc: {
		id: 56,
		name: 'BNB Chain',
		usdt: '0x55d398326f99059fF775485246999027B3197955', // USDT (18 decimals)
		explorer: 'https://bscscan.com',
	},
	base: {
		id: 8453,
		name: 'Base',
		usdc: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', // Native USDC
		explorer: 'https://basescan.org',
	},
	// Entry chain for Hyperliquid HIP-4. Internal Bridge routes USDC here;
	// the auto-deposit watcher (useHyperliquidAutoDeposit) sees the arrival
	// on user's EOA and runs Bridge2.deposit to land funds on HyperCore.
	arbitrum: {
		id: 42161,
		name: 'Arbitrum',
		usdc: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831', // Native USDC
		explorer: 'https://arbiscan.io',
	},
} as const

export type SupportedChainId = 137 | 792703809 | 56 | 8453 | 42161

// API Types

export type RelayQuoteParams = {
	user: string
	originChainId: number
	originCurrency: string
	destinationChainId: number
	destinationCurrency: string
	amount: string
	recipient?: string
	tradeType?: 'EXACT_INPUT' | 'EXACT_OUTPUT'
}

export type RelayMultiInputOrigin = {
	chainId: number
	currency: string
	amount: string
	user?: string
}

export type RelayMultiInputParams = {
	user: string
	origins: RelayMultiInputOrigin[]
	destinationChainId: number
	destinationCurrency: string
	recipient?: string
	tradeType?: 'EXACT_INPUT' | 'EXACT_OUTPUT'
}

export type RelayFee = {
	currency: {
		chainId: number
		address: string
		symbol: string
		name: string
		decimals: number
	}
	amount: string
	amountFormatted: string
	amountUsd: string
}

export type RelayStep = {
	id: string
	action: string
	description: string
	kind: 'transaction' | 'signature'
	requestId?: string
	items: Array<{
		status: 'incomplete' | 'complete'
		data?: {
			chainId?: number
			to?: string
			from?: string
			data?: string
			value?: string
			maxFeePerGas?: string
			maxPriorityFeePerGas?: string
			gas?: number
			// Solana instructions
			instructions?: Array<{
				keys: Array<{ pubkey: string; isSigner: boolean; isWritable: boolean }>
				programId: string
				data: string
			}>
		}
		check?: {
			endpoint: string
			method: string
		}
	}>
}

export type RelayQuoteDetails = {
	sender: string
	recipient: string
	currencyIn: {
		currency: {
			chainId: number
			address: string
			symbol: string
			name: string
			decimals: number
		}
		amount: string
		amountFormatted: string
		amountUsd: string
	}
	currencyOut: {
		currency: {
			chainId: number
			address: string
			symbol: string
			name: string
			decimals: number
		}
		amount: string
		amountFormatted: string
		amountUsd: string
	}
	timeEstimate?: number
	totalImpact?: {
		usd: string
		percent: string
	}
}

export type RelayQuoteResponse = {
	steps: RelayStep[]
	fees: {
		gas?: RelayFee
		relayer?: RelayFee
		relayerGas?: RelayFee
		relayerService?: RelayFee
		app?: RelayFee
	}
	details: RelayQuoteDetails
}

export type RelayStatusResponse = {
	requestId: string
	status: 'pending' | 'success' | 'failure' | 'refund'
	user: string
	recipient: string
	data?: {
		inTxHashes?: string[]
		outTxHashes?: string[]
		failReason?: string
	}
	originChainId: number
	destinationChainId: number
	createdAt: string
	updatedAt: string
}

// API Functions

/**
 * Get a quote for a single-origin swap/bridge
 */
export async function getRelayQuote(params: RelayQuoteParams): Promise<RelayQuoteResponse> {
	const res = await fetch(`${RELAY_BASE_URL}/quote/v2`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(params),
	})

	if (!res.ok) {
		const error = await res.json().catch(() => ({ message: res.statusText }))
		throw new Error(error.message || `Relay API error: ${res.status}`)
	}

	return res.json()
}

/**
 * Get a quote for multi-origin swap (combining balances from multiple chains)
 */
export async function getRelayMultiInputQuote(
	params: RelayMultiInputParams
): Promise<RelayQuoteResponse> {
	const res = await fetch(`${RELAY_BASE_URL}/execute/swap/multi-input`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(params),
	})

	if (!res.ok) {
		const error = await res.json().catch(() => ({ message: res.statusText }))
		throw new Error(error.message || `Relay API error: ${res.status}`)
	}

	return res.json()
}

/**
 * Get the status of a relay transaction
 */
export async function getRelayStatus(requestId: string): Promise<RelayStatusResponse> {
	const res = await fetch(
		`${RELAY_BASE_URL}/intents/status/v3?requestId=${encodeURIComponent(requestId)}`,
		{
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		}
	)

	if (!res.ok) {
		const error = await res.json().catch(() => ({ message: res.statusText }))
		throw new Error(error.message || `Relay API error: ${res.status}`)
	}

	return res.json()
}

// Helper Functions

/**
 * Get the USDC address for a given chain ID
 */
export function getStablecoinAddress(chainId: number): string {
	if (chainId === CHAIN_CONFIG.polygon.id) {
		return CHAIN_CONFIG.polygon.usdc
	}
	if (chainId === CHAIN_CONFIG.solana.id) {
		return CHAIN_CONFIG.solana.usdc
	}
	if (chainId === CHAIN_CONFIG.bsc.id) {
		return CHAIN_CONFIG.bsc.usdt
	}
	if (chainId === CHAIN_CONFIG.base.id) {
		return CHAIN_CONFIG.base.usdc
	}
	if (chainId === CHAIN_CONFIG.arbitrum.id) {
		return CHAIN_CONFIG.arbitrum.usdc
	}
	throw new Error(`Unsupported chain ID: ${chainId}`)
}

/** @deprecated Use getStablecoinAddress instead */
export const getUsdcAddress = getStablecoinAddress

export type RelayChainCall = {
	target: `0x${string}`
	data: `0x${string}`
	value?: bigint
}

/**
 * Filter a Relay quote's steps down to the EVM calls scheduled on a
 * specific chain. The Relay quote returns interleaved steps across
 * multiple chains; for sponsored userOps we only want the calls bound
 * to one chain at a time.
 */
export function extractRelayOpsForChain(steps: RelayStep[], chainId: number): RelayChainCall[] {
	const ops: RelayChainCall[] = []
	for (const step of steps) {
		for (const item of step.items) {
			if (item.data?.chainId === chainId && item.data?.to && item.data?.data) {
				ops.push({
					target: item.data.to as `0x${string}`,
					data: item.data.data as `0x${string}`,
					value: item.data.value ? BigInt(item.data.value) : undefined,
				})
			}
		}
	}
	return ops
}

/**
 * Get the block explorer URL for a transaction
 */
export function getExplorerUrl(chainId: number, txHash: string): string {
	if (chainId === CHAIN_CONFIG.polygon.id) {
		return `${CHAIN_CONFIG.polygon.explorer}/tx/${txHash}`
	}
	if (chainId === CHAIN_CONFIG.solana.id) {
		return `${CHAIN_CONFIG.solana.explorer}/tx/${txHash}`
	}
	if (chainId === CHAIN_CONFIG.bsc.id) {
		return `${CHAIN_CONFIG.bsc.explorer}/tx/${txHash}`
	}
	if (chainId === CHAIN_CONFIG.base.id) {
		return `${CHAIN_CONFIG.base.explorer}/tx/${txHash}`
	}
	if (chainId === CHAIN_CONFIG.arbitrum.id) {
		return `${CHAIN_CONFIG.arbitrum.explorer}/tx/${txHash}`
	}
	return ''
}
